import React, { useState, useRef, useEffect } from 'react';
import Box from '@mui/material/Box';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import DeleteIcon from '@mui/icons-material/Delete';
import Tooltip from '@mui/material/Tooltip';
import AddIcon from '@mui/icons-material/Add';
import InfoIcon from '@mui/icons-material/Info';
import NetworkDomain from "common/values/network-domain/network-domain";
import { styled } from "@mui/material/styles";

const StyledTopBox = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(0.5),
}));

const StyledErrorBox = styled(Box)(({ theme }) => ({
    minHeight: 24,
    marginBottom: theme.spacing(1),
}));

const StyledErrorText = styled(Typography)(({ theme }) => ({
    color: theme.palette.error.main,
}));

const StyledListBox = styled(Box)(({ theme }) => ({
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.shape.borderRadius,
    boxShadow: theme.shadows[1],
    padding: theme.spacing(1),
    marginBottom: theme.spacing(1),
    border: `1px solid ${theme.palette.divider}`,
    minHeight: 48,
    maxHeight: `${48 * 5 + 4}px`, // 5 items (48px each) + 4 gaps (0.5 spacing each, approximated as 1px per gap)
    overflowY: 'auto',
}));

const StyledDomainList = styled(List)(() => ({
    padding: 0,
}));

const StyledListItem = styled(ListItem)(({ theme }) => ({
    borderRadius: theme.shape.borderRadius,
    marginBottom: theme.spacing(0.5),
    paddingLeft: theme.spacing(1),
    paddingRight: theme.spacing(1),
    backgroundColor: theme.palette.grey[50],
    '&:last-child': { marginBottom: 0 },
}));

const ReadonlyDomainBox = styled(Box)(({ theme }) => ({
    backgroundColor: theme.palette.action.disabledBackground,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(1, 2),
    marginBottom: theme.spacing(1),
    color: theme.palette.text.secondary,
    fontSize: theme.typography.body2.fontSize,
    display: 'flex',
    alignItems: 'center',
    minHeight: 32,
    border: `1px solid ${theme.palette.divider}`,
}));

export interface NetworkDomainListProps {
    domains: string[];
    onChange: (domains: string[]) => void;
    creatorEmail?: string;
}


const NetworkDomainList: React.FC<Readonly<NetworkDomainListProps>> = ({ domains, onChange, creatorEmail }) => {
    const [input, setInput] = useState('');
    const [error, setError] = useState<string | null>(null);
    const listBoxRef = useRef<HTMLDivElement>(null);
    const lastItemRef = useRef<HTMLLIElement>(null);
    const prevDomainsLength = useRef(domains.length);

    const creatorEmailDomain = creatorEmail ? creatorEmail.split('@')[1] : null;

    useEffect(() => {
        if (domains.length > prevDomainsLength.current) {
            if (listBoxRef.current) {
                listBoxRef.current.scrollTop = listBoxRef.current.scrollHeight;
            }
        }
        prevDomainsLength.current = domains.length;
    }, [domains.length]);

    const handleAdd = () => {
        try {
            const domainInstance = new NetworkDomain(input); //this calls validate internally
            const value = domainInstance.value;
            if (domains.includes(value)) {
                setError('Domain already exists.');
                return;
            }
            setError(null);
            onChange([...domains, value]);
            setInput('');
        } catch (e) {
            if (e instanceof Error) {
                setError(e.message);
            }
        }
    };

    const handleRemove = (domain: string) => {
        if (domains.length <= 1) return;
        onChange(domains.filter(d => d !== domain));
    };

    return (
        <Box>
            {creatorEmailDomain && (
                <ReadonlyDomainBox>
                    Your email domain is automatically added: <b style={{ marginLeft: 4 }}>{creatorEmailDomain}</b>
                </ReadonlyDomainBox>
            )}
            <StyledTopBox>
                <TextField
                    label="Add Domain"
                    value={input}
                    onChange={e => { setInput(e.target.value); setError(null); }}
                    error={!!error}
                    size="small"
                    onKeyDown={e => { if (e.key === 'Enter') handleAdd(); }}
                />
                <Button
                    variant="contained"
                    color="primary"
                    startIcon={<AddIcon />}
                    onClick={handleAdd}
                    disabled={!input.trim()}
                >
                    Add
                </Button>
                <Tooltip title="A valid domain is required, such as example.com, or test.net." arrow>
                    <IconButton size="small" sx={{ ml: 1 }} tabIndex={-1}>
                        <InfoIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
            </StyledTopBox>
            <StyledErrorBox>
                {error && (
                    <StyledErrorText variant="caption">{error}</StyledErrorText>
                )}
            </StyledErrorBox>
            {domains.length > 0 && (
                <StyledListBox ref={listBoxRef}>
                    <StyledDomainList>
                        {domains.map((domain, idx) => (
                            <StyledListItem
                                key={domain}
                                ref={idx === domains.length - 1 ? lastItemRef : undefined}
                                secondaryAction={
                                    <Tooltip title={domains.length === 1 ? "At least one domain required" : "Remove domain"} arrow>
                                        <span>
                                            <IconButton edge="end" aria-label="delete" onClick={() => handleRemove(domain)} disabled={domains.length === 1}>
                                                <DeleteIcon sx={{ color: domains.length === 1 ? 'grey.400' : 'error.main' }} />
                                            </IconButton>
                                        </span>
                                    </Tooltip>
                                }
                            >
                                <ListItemText primary={domain} />
                            </StyledListItem>
                        ))}
                    </StyledDomainList>
                </StyledListBox>
            )}
        </Box>
    );
};

export default NetworkDomainList;

